// src/components/corkboard/CorkDetailsPanel.jsx
//
// The right rail of the Cork Board: everything about the selected card, and
// every edit the ticket asks for — rename it, describe it, see and remove its
// relationships, add new ones, delete it.
//
// **Why this is a fork of editor/StoryElementDetails.jsx and not a reuse.**
// That component does the same five things, and reusing it was the obvious
// first thought. It is built for a 280px dark sidebar whose container ships
// global CSS forcing any MUI Button to full-width gold — which is why every
// control in it is a plain <Box> — and generalising it to work on a light
// full-width page means reaching back into the editor's Story Elements tab and
// putting shipped, tested work at risk to save a component. The two panels
// share what actually matters: the same board model, the same endpoints, and
// the same pure helpers in utils/storyElements.js. If they ever drift in
// *behaviour* rather than in styling, that is the bug — not the duplication.
//
// Edits autosave on a debounce, same as the editor's panel and for the same
// reason: there is no Save button on a board you navigate away from.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, IconButton, MenuItem, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { connectTargets, relationshipsFor } from '../../utils/storyElements';
import { CB, FONT, TYPE_COLOR, TYPE_LABEL, eyebrowSx } from './corkTokens';

const SAVE_DEBOUNCE_MS = 450;

const fieldSx = {
  mt: 1,
  '& .MuiOutlinedInput-root': {
    fontFamily: FONT.sans,
    fontSize: 12.5,
    backgroundColor: CB.white,
    '& fieldset': { borderColor: CB.lineSoft },
    '&:hover fieldset': { borderColor: CB.purpleSoft },
    '&.Mui-focused fieldset': { borderColor: CB.purple },
  },
};

export default function CorkDetailsPanel({
  node,                   // the selected node, or null
  board,                  // { nodes, edges }
  chapters,               // chaptersFromHtml() result
  onPatchNode,            // (id, patch) => void — local only, keeps the card in step
  onSaveNode,             // (id, fields) => void — debounced, writes
  onRemoveNode,           // (id) => void
  onAddRelationship,      // ({ from, to, label }) => void
  onRemoveRelationship,   // (edgeId) => void
}) {
  const [newTarget, setNewTarget] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const saveTimer = useRef(null);

  const ref = node ? `node:${node._id}` : null;

  const relationships = useMemo(
    () => (ref ? relationshipsFor(ref, { edges: board.edges, nodes: board.nodes, chapters }) : []),
    [ref, board.edges, board.nodes, chapters],
  );

  const targets = useMemo(
    () => connectTargets({ nodes: board.nodes, chapters, excludeRef: ref })
      // Something already related to this element is not a target for a second
      // relationship — the prototype's select hides them, and offering one
      // would produce a duplicate string between the same two pins.
      .filter((t) => !relationships.some((r) => r.other.ref === t.value)),
    [board.nodes, chapters, ref, relationships],
  );

  // Pending edits accumulate rather than replace. Renaming a card and then
  // typing in its description within the debounce window is two calls here, and
  // a timer that carried only the latest `fields` would save the description
  // and silently drop the new name.
  const pending = useRef(null); // { id, fields }

  const flush = useCallback(() => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    const p = pending.current;
    pending.current = null;
    if (p) onSaveNode?.(p.id, p.fields);
  }, [onSaveNode]);

  const scheduleSave = useCallback((id, fields) => {
    // An edit to a different card than the one waiting means the author moved
    // on: write what is pending now rather than merging two cards' fields.
    if (pending.current && pending.current.id !== id) flush();
    pending.current = { id, fields: { ...(pending.current?.fields || {}), ...fields } };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
  }, [flush]);

  // Unmounting — navigating away mid-edit — must write, not discard. This is
  // the case the debounce exists to survive.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => () => flushRef.current(), []);

  // Switching cards commits the previous one's edit immediately, so nothing is
  // left in a timer belonging to a card the author can no longer see.
  useEffect(() => {
    flushRef.current();
    setNewTarget('');
    setNewLabel('');
  }, [node?._id]);

  if (!node) {
    return (
      <Box sx={{ p: 2.75 }}>
        <Typography component="span" sx={eyebrowSx}>Details</Typography>
        <Typography sx={{
          fontFamily: FONT.mono, fontSize: 11.5, color: CB.gray, lineHeight: 1.7, mt: 1,
        }}>
          Select a card to see its description and everything it is connected to.
          Drag cards to rearrange the board, or use the buttons in the corner to
          add something new.
        </Typography>
      </Box>
    );
  }

  const handleField = (field) => (e) => {
    const value = e.target.value;
    onPatchNode?.(node._id, { [field]: value });
    scheduleSave(node._id, { [field]: value });
  };

  const addRelationship = () => {
    if (!newTarget) return;
    onAddRelationship?.({
      from: ref,
      to: newTarget,
      label: newLabel.trim() || 'connected to',
    });
    setNewTarget('');
    setNewLabel('');
  };

  return (
    <Box sx={{ p: 2.75 }}>
      <Typography component="span" sx={{
        ...eyebrowSx, color: TYPE_COLOR[node.type] || CB.goldDeep,
      }}>
        {TYPE_LABEL[node.type] || node.type}
      </Typography>

      <TextField
        value={node.name || ''}
        onChange={handleField('name')}
        placeholder="Untitled"
        variant="outlined"
        size="small"
        fullWidth
        inputProps={{ 'aria-label': 'Name' }}
        sx={{
          ...fieldSx,
          '& .MuiOutlinedInput-root': {
            ...fieldSx['& .MuiOutlinedInput-root'],
            fontFamily: FONT.serif,
            fontSize: 18,
            fontWeight: 600,
          },
        }}
      />

      <Typography component="label" sx={{ ...eyebrowSx, display: 'block', mt: 2.5 }}>
        Description
      </Typography>
      <TextField
        value={node.desc || ''}
        onChange={handleField('desc')}
        placeholder={`What is this ${(TYPE_LABEL[node.type] || 'element').toLowerCase()}?`}
        variant="outlined"
        size="small"
        fullWidth
        multiline
        minRows={3}
        sx={{ ...fieldSx, mt: 0.75 }}
      />

      <Typography component="span" sx={{ ...eyebrowSx, display: 'block', mt: 3 }}>
        Connected to ({relationships.length})
      </Typography>

      {relationships.length === 0 ? (
        <Typography sx={{ fontFamily: FONT.mono, fontSize: 11, color: CB.gray, mt: 1 }}>
          No connections yet.
        </Typography>
      ) : relationships.map(({ edge, other }) => (
        <Box
          key={edge._id}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            backgroundColor: CB.lavender, borderRadius: '4px',
            px: 1.25, py: 1, mt: 0.75,
          }}
        >
          <Box sx={{
            width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
            // A relationship whose other end the author deleted still renders —
            // greyed rather than hidden, so the way to clear it is visible.
            backgroundColor: other.missing
              ? CB.lineSoft
              : (TYPE_COLOR[other.type] || CB.gold),
          }} />
          <Box sx={{
            flex: 1, minWidth: 0, fontFamily: FONT.sans, fontSize: 12,
            color: other.missing ? CB.gray : CB.black,
            fontStyle: other.missing ? 'italic' : 'normal',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {other.name}
          </Box>
          {edge.label ? (
            <Box sx={{
              fontFamily: FONT.mono, fontSize: 9.5, color: CB.gray, flexShrink: 0,
            }}>
              {edge.label}
            </Box>
          ) : null}
          <IconButton
            size="small"
            aria-label={`Remove connection to ${other.name}`}
            onClick={() => onRemoveRelationship?.(edge._id)}
            sx={{ p: 0.25, color: CB.gray, '&:hover': { color: CB.danger } }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}

      <Box sx={{
        mt: 1.5, p: 1.5, backgroundColor: CB.lavender,
        border: `1px dashed ${CB.lineSoft}`, borderRadius: '4px',
      }}>
        <Typography component="label" sx={{ ...eyebrowSx, display: 'block' }}>
          Add another connection
        </Typography>
        <TextField
          select
          value={newTarget}
          onChange={(e) => setNewTarget(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          sx={{ ...fieldSx, mt: 0.75 }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="" disabled>
            {targets.length ? 'Choose an element…' : 'Nothing left to connect to'}
          </MenuItem>
          {targets.map((t) => (
            <MenuItem key={t.value} value={t.value} sx={{ fontSize: 12.5 }}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addRelationship(); }}
          placeholder="Relationship (e.g. appears in)"
          variant="outlined"
          size="small"
          fullWidth
          sx={{ ...fieldSx, mt: 0.75 }}
        />
        <Button
          onClick={addRelationship}
          disabled={!newTarget}
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          fullWidth
          sx={{
            mt: 1, fontFamily: FONT.mono, fontSize: 11, textTransform: 'none',
            color: CB.purple, border: `1px solid ${CB.lineSoft}`,
            backgroundColor: CB.white,
            '&:hover': { borderColor: CB.purple, backgroundColor: CB.white },
          }}
        >
          Add connection
        </Button>
      </Box>

      <Button
        onClick={() => onRemoveNode?.(node._id)}
        startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
        fullWidth
        sx={{
          mt: 3, fontFamily: FONT.mono, fontSize: 11, textTransform: 'none',
          color: CB.danger, border: '1px solid #EBCBD4', backgroundColor: CB.white,
          '&:hover': { backgroundColor: '#FBEEF0', borderColor: CB.danger },
        }}
      >
        Remove this {(TYPE_LABEL[node.type] || 'element').toLowerCase()}
      </Button>
    </Box>
  );
}
